import axios from "axios";

const API_BASE_URL =
  "https://s59p31pkej.execute-api.us-east-1.amazonaws.com/dev/";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add Basic Auth to requests (for admin)
api.interceptors.request.use((config) => {
  const adminToken = localStorage.getItem("adminToken");
  const userToken = localStorage.getItem("userToken");

  // Admin requests use Basic Auth
  if (adminToken) {
    config.headers.Authorization = `Basic ${adminToken}`;
  } else if (userToken) {
    // User requests use Bearer token
    config.headers.Authorization = `Bearer ${userToken}`;
  }

  return config;
});

export interface Category {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ColorVariation {
  color: string; // Color name (e.g., "Red", "Blue", "Green")
  image: string; // Image URL for this color variation
}

export interface Item {
  id: string;
  name: string;
  description: string;
  categoryId: string;
  size: string;
  mainImage?: string; // All design image
  subMainImage?: string; // Design image
  colorVariations?: ColorVariation[]; // Variations with color names and images
  images?: string[]; // Legacy support - will be replaced by above
  price: number; // Current selling price
  mrp?: number; // Maximum Retail Price (for discount calculation)
  stock: number; // Stock quantity (0 = out of stock)
  isOutOfStock?: boolean; // Convenience flag (computed from stock === 0)
  createdAt: string;
  updatedAt?: string;
}

export interface ContactForm {
  name: string;
  phone: string;
  email?: string;
  message?: string;
}

export interface PresignedUrlResponse {
  presignedUrl: string;
  key: string;
  imageUrl: string;
}

// Categories API
export const categoriesApi = {
  getAll: () => api.get("/api/categories"),
  create: (category: Omit<Category, "id" | "createdAt">) =>
    api.post("/api/categories", category),
  update: (id: string, category: Partial<Category>) =>
    api.put(`/api/categories/${id}`, category),
  delete: (id: string) => api.delete(`/api/categories/${id}`),
};

// Products API (renamed from Items)
export const productsApi = {
  getAll: (categoryId?: string) =>
    api.get(`/api/products${categoryId ? `?categoryId=${categoryId}` : ""}`),
  create: (product: Omit<Item, "id" | "createdAt">) =>
    api.post("/api/products", product),
  update: (id: string, product: Partial<Item>) =>
    api.put(`/api/products/${id}`, product),
  delete: (id: string) => api.delete(`/api/products/${id}`),
};

// Legacy Items API (for backward compatibility)
export const itemsApi = productsApi;

// Upload API
export const uploadApi = {
  getPresignedUrl: (fileName: string, fileType: string) =>
    api.post("/api/upload/presigned-url", { fileName, fileType }),
};

// Contact API
export const contactApi = {
  submit: (formData: ContactForm) => api.post("/api/contact", formData),
};

// Cart Item interface (for localStorage)
export interface CartItem {
  productId: string;
  product: Item; // Store full product details
  quantity: number;
  selectedColor?: string;
  selectedSize?: string;
}

export interface Order {
  id: string;
  userId: string;
  userEmail?: string;
  products: Array<{
    productId: string;
    productName?: string;
    quantity: number;
    price: number;
    selectedColor?: string;
    selectedSize?: string;
  }>;
  totalAmount: number;
  shippingCharges?: number;
  paymentDate: string;
  paymentMode: string;
  shippingAddress?: string;
  status: string;
  trackingId?: string;
  courierPartner?: string;
  createdAt: string;
  updatedAt?: string;
}

// Orders API
export const ordersApi = {
  create: (orderData: {
    userId: string;
    userEmail?: string;
    products: Array<{
      productId: string;
      quantity: number;
      price: number;
      selectedColor?: string;
      selectedSize?: string;
    }>;
    paymentDate: string;
    paymentMode: string;
    shippingAddress?: string;
    shippingCharges?: number;
    totalAmount?: number;
    status?: string;
  }) => api.post("/api/orders", orderData),
  getAll: (userId?: string) =>
    api.get(`/api/orders${userId ? `?userId=${userId}` : ""}`),
  getById: (id: string) => api.get(`/api/orders/${id}`),
  update: (
    id: string,
    orderData: {
      status?: string;
      trackingId?: string;
      paymentDate?: string;
      paymentMode?: string;
      shippingAddress?: string;
      courierPartner?: string;
    }
  ) => api.put(`/api/orders/${id}`, orderData),
};

// Auth API (Admin)
export const authApi = {
  login: async (username: string, password: string) => {
    try {
      // Validate credentials with backend
      const response = await api.post('/api/admin/login', {
        username,
        password
      });

      if (response.data && response.data.authenticated) {
        // Store token only after successful validation
    const token = btoa(`${username}:${password}`);
    localStorage.setItem("adminToken", token);
        return { token };
      } else {
        throw new Error('Invalid credentials');
      }
    } catch (error: any) {
      // Clear any existing token on error
      localStorage.removeItem("adminToken");
      throw error;
    }
  },
  logout: () => {
    localStorage.removeItem("adminToken");
  },
  isAuthenticated: () => {
    return !!localStorage.getItem("adminToken");
  },
};

// User Auth API
export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
}

export const userAuthApi = {
  checkEmail: (data: { email: string }) =>
    api.post("/api/auth/check-email", data),
  requestOTP: (data: { email: string; name?: string; phone?: string }) =>
    api.post("/api/auth/request-otp", data),
  verifyOTP: (data: { email: string; otp: string }) =>
    api.post("/api/auth/verify-otp", data),
  getCurrentUser: () => api.get("/api/auth/me"),
  logout: async () => {
    // Make API call first to invalidate token on server (needs token in header)
    try {
      await api.post("/api/auth/logout");
    } catch (error) {
      // Continue with logout even if API call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Remove token from localStorage after API call
    localStorage.removeItem("userToken");
    localStorage.removeItem("user");
    }
  },
  isAuthenticated: () => {
    return !!localStorage.getItem("userToken");
  },
  getStoredUser: (): User | null => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  },
};

export default api;
